import { v4 as uuidv4 } from 'uuid';

import type { IBaseLinkDataField } from 'shared/types/links/Interfaces/template/shared/IBaseFields';
import { QR_CODE_LINK_DATA_BLACKLIST } from '../../../../shared/constants';
import type { LinkType } from '../../../../shared/types';
import { ADS_PRESET_FIELDS, PRESET_FIELDS } from '../reducers/LinkCreatorReducer/utils';
import { LinkTemplateFieldValueType } from '../types/linkTypes';
import type { LinkDataObject } from '../types/links';
import { LinkTemplateRulesService } from './LinkTemplateRulesService/LinkTemplateRulesService';

export type ScrapedLinkData = Record<string, any>;

export type BaseLinkDataWithOptions = IBaseLinkDataField & {
  options?: string[];
};

export class LinkDataService {
  private static getLinkDataBlacklist(linkType: LinkType) {
    switch (linkType) {
      case 'ad': {
        return ADS_PRESET_FIELDS;
      }
      case 'quick': {
        return PRESET_FIELDS;
      }
      case 'qrCode': {
        return QR_CODE_LINK_DATA_BLACKLIST;
      }
    }
  }

  /**
   * Creates a LinkDataObject based on the provided link data item.
   *
   * @param {BaseLinkDataWithOptions} linkDataItem - The link data item to be transformed into a LinkDataObject.
   * @returns {LinkDataObject} The created LinkDataObject.
   */
  public static getLinkDataObject = (linkDataItem: BaseLinkDataWithOptions): LinkDataObject => {
    const { fieldName, value, valueType, required, options = [] } = linkDataItem;
    return {
      id: uuidv4(),
      key: { label: fieldName, value: fieldName },
      value: typeof value === 'string' ? value : undefined,
      options: typeof value === 'object' ? value : options,
      suggestions: valueType === LinkTemplateFieldValueType.SUGGESTIONS,
      choose: valueType === LinkTemplateFieldValueType.CHOOSE,
      empty: valueType === LinkTemplateFieldValueType.EMPTY,
      fixed: valueType === LinkTemplateFieldValueType.FIXED,
      required: required,
    };
  };

  /**
   * Maps an array of template link data to an array of LinkDataObject objects.
   *
   * @param {BaseLinkDataWithOptions[]} templateLinkData - The array of template link data.
   * @returns {LinkDataObject[]} linkDataArray - An array of LinkDataObject objects.
   */
  public static mapTemplateLinkData = (templateLinkData: BaseLinkDataWithOptions[]): LinkDataObject[] => {
    return templateLinkData.map(this.getLinkDataObject);
  };

  /**
   * Merges two arrays of objects based on key.value path, removing duplicates.
   * The second duplicated element encountered is the one kept.
   *
   * @param {LinkDataObject[]} arr1 - The first array to be merged.
   * @param {LinkDataObject[]} arr2 - The second array to be merged.
   * @returns {LinkDataObject[]} A new array containing unique elements from both input arrays based on the specified property path.
   *
   * @example
   * const arr1 = [
   *   { key: { value: 'valueToMatch', label: 'some label' }, title: 'title' },
   *   { key: { value: 'some value', label: 'some label2' }, title: 'title2' },
   * ];
   *
   * const arr2 = [
   *   { key: { value: 'valueToMatch', label: 'some label overriden' }, title: 'title overriden' },
   * ];
   *
   * mergeLinkDataArraysByKey(arr2, arr1);
   * returns: [
   *    { key: { value: 'valueToMatch', label: 'some label overriden' }, title: 'title overriden' },
   *    { key: { value: 'some value', label: 'some label2' }, title: 'title2' },
   *  ]
   */
  public static mergeLinkDataArraysByKey = (arr1: LinkDataObject[], arr2: LinkDataObject[]): LinkDataObject[] => {
    const KEY_PATH = ['key', 'value'];

    const getProperty = (obj) => KEY_PATH.reduce((acc, key) => acc?.[key], obj);

    const arr: any = [];
    const seenProperty: Record<string, number> = {};

    arr1.concat(arr2).forEach((obj, idx) => {
      const property = getProperty(obj);
      if (property === undefined) return;
      if (seenProperty[property] === undefined) {
        seenProperty[property] = idx;
        arr.push(obj);
      } else {
        arr[seenProperty[property]] = obj;
      }
    });

    return arr;
  };

  /**
   * Excludes preset fields from the given LinkCreatorData object.
   *
   * @param {ScrapedLinkData} linkCreatorData - The input LinkCreatorData object.
   * @returns {ScrapedLinkData} linkCreatorDataExcludingPresets - The LinkCreatorData object excluding preset fields.
   */
  public static excludePresetsFromLinkCreatorData = (linkCreatorData: ScrapedLinkData, linkType: LinkType) => {
    const presets = this.getLinkDataBlacklist(linkType);
    const linkCreatorDataExcludingPresets: ScrapedLinkData = {};
    Object.keys(linkCreatorData).forEach((key) => {
      if (!presets.includes(key)) {
        linkCreatorDataExcludingPresets[key] = linkCreatorData[key];
      }
    });
    return linkCreatorDataExcludingPresets;
  };

  /**
   * Maps ScrapedLinkData to an array of LinkDataObject items.
   *
   * @param {ScrapedLinkData} linkCreatorData - The input LinkCreatorData object.
   * @returns {LinkDataObject[]} linkDataArray - An array of LinkDataObject items.
   */
  public static mapLinkCreatorDataToLinkDataObject = (linkCreatorData: ScrapedLinkData): LinkDataObject[] => {
    return Object.entries(linkCreatorData).map(([key, value]) => {
      return { id: uuidv4(), key: { label: key, value: key }, value };
    });
  };

  /**
   * Maps ScrapedLinkData to an array of objects representing TemplateLinkData.
   *
   * @param {ScrapedLinkData} linkCreatorData - The input ScrapedLinkData object.
   * @returns {TemplateLinkData[]} templateLinkDataArray - An array of objects representing TemplateLinkData.
   */
  public static mapLinkCreatorDataToTemplateLinkData = (linkCreatorData: ScrapedLinkData) =>
    Object.entries(linkCreatorData).map(([fieldName, value]) => ({ fieldName, value }));

  /**
   * Filters scraped link data based on the provided template link data.
   *
   * @param {ScrapedLinkData} scrapedLinkData - The scraped link data.
   * @param {IBaseLinkDataField[]} templateArr - The array of template link data.
   * @returns {any[]} filteredArr - The filtered array of link data.
   */
  public static filterScrapedLinkDataWithTemplateLinkData = (
    scrapedLinkData: ScrapedLinkData,
    templateArr: IBaseLinkDataField[]
  ) => {
    const arr: BaseLinkDataWithOptions[] = [];

    Object.entries(scrapedLinkData).forEach(([key, value]) => {
      const templateLinkDataItem = templateArr.find((item) => item.fieldName === key);

      if (templateLinkDataItem) {
        const { valueType, value: templateLinkDataItemValue } = templateLinkDataItem;

        let overrideAttr;

        const isAny = LinkTemplateRulesService.isValueTypeAny(valueType);
        const isChoose = LinkTemplateRulesService.isValueTypeChoose(valueType);
        const isSuggestions = LinkTemplateRulesService.isValueTypeSuggestions(valueType);

        const isValueIncludedInTemplate = templateLinkDataItemValue?.includes(value);

        if ((isChoose && isValueIncludedInTemplate) || isSuggestions) {
          overrideAttr = {
            options: templateLinkDataItemValue,
            value: value,
          };
        } else if (isAny) {
          overrideAttr = { value };
        }

        arr.push({ ...templateLinkDataItem, ...overrideAttr });
      } else {
        arr.push({ fieldName: key, valueType: 'any', value, required: false });
      }
    });

    return arr;
  };
}
